/**
 * Recent movement-event history for the developer panel. Events are inferred
 * after each tick from state transitions, so the log observes the simulation
 * without adding hooks to the motor. Detection is diagnostic, not
 * authoritative: thresholds are chosen to be readable, and nothing in gameplay
 * depends on them.
 */
import type { Simulation } from '../simulation/Simulation';

export type MovementEventKind = 'jump' | 'release' | 'land' | 'launch' | 'impact' | 'death' | 'respawn';

export interface MovementEvent {
  tick: number;
  kind: MovementEventKind;
  /** Planar speed when the event happened (m/s). */
  speed: number;
  detail: string;
}

/** Speed lost in one tick (m/s) that counts as an impact. */
export const IMPACT_SPEED_LOSS = 3;
const COMPRESSION_KEY = 'exp.compression';

export class MovementEventLog {
  private readonly ring: MovementEvent[] = [];
  private prevGrounded = false;
  private prevSpeed = 0;
  private prevVy = 0;
  private prevCharge = 0;
  private readonly unsubscribe: (() => void)[] = [];

  constructor(
    private readonly sim: Simulation,
    readonly capacity = 14,
  ) {
    this.unsubscribe.push(
      sim.events.on('death', (e) => this.push({ tick: e.tick, kind: 'death', speed: this.prevSpeed, detail: e.cause })),
      sim.events.on('respawn', (e) => {
        this.push({ tick: sim.state.tick, kind: 'respawn', speed: 0, detail: e.reason });
        this.sync();
      }),
    );
    this.sync();
  }

  /** Call once after every simulation tick. */
  observe(): void {
    const st = this.sim.state;
    if (!st.alive) return;
    const v = st.velocity;
    const speed = Math.hypot(v.x, v.z);
    const total = Math.hypot(v.x, v.y, v.z);
    const prevTotal = Math.hypot(this.prevSpeed, this.prevVy);
    const charge = this.charge();
    if (st.ticksSinceJump === 1) {
      const released = this.prevCharge > 0 && charge === 0;
      this.push({ tick: st.tick, kind: released ? 'release' : 'jump', speed, detail: released ? `compression +${this.prevCharge.toFixed(1)} m/s` : `vy ${v.y.toFixed(1)}` });
    } else if (!this.prevGrounded && st.grounded) {
      this.push({ tick: st.tick, kind: 'land', speed, detail: `fell at ${(-this.prevVy).toFixed(1)} m/s` });
    } else if (this.prevGrounded && !st.grounded) {
      this.push({ tick: st.tick, kind: 'launch', speed, detail: `left ground, vy ${v.y.toFixed(1)}` });
    }
    if (prevTotal - total > IMPACT_SPEED_LOSS && !(!this.prevGrounded && st.grounded)) {
      const stored = charge > this.prevCharge ? `, stored ${charge.toFixed(1)}` : '';
      this.push({ tick: st.tick, kind: 'impact', speed, detail: `lost ${(prevTotal - total).toFixed(1)} m/s${stored}` });
    }
    this.prevGrounded = st.grounded;
    this.prevSpeed = speed;
    this.prevVy = v.y;
    this.prevCharge = charge;
  }

  /** Newest first. */
  recent(): readonly MovementEvent[] {
    return [...this.ring].reverse();
  }

  clear(): void {
    this.ring.length = 0;
  }

  dispose(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
  }

  private charge(): number {
    const s = this.sim.state.ruleState.get(COMPRESSION_KEY);
    return s ? (s.snapshot()[0] ?? 0) : 0;
  }

  private sync(): void {
    const st = this.sim.state;
    this.prevGrounded = st.grounded;
    this.prevSpeed = Math.hypot(st.velocity.x, st.velocity.z);
    this.prevVy = st.velocity.y;
    this.prevCharge = this.charge();
  }

  private push(e: MovementEvent): void {
    this.ring.push(e);
    if (this.ring.length > this.capacity) this.ring.shift();
  }
}
